import React, { useEffect, useState } from 'react';
import { Modal, Spinner, Button, Form } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { apiHandler, getApiErrorMessage } from '../../api/apihandler';
import { TEACHER_GAME_REPORT_PARENT_NOTE, TEACHER_GAME_REPORT_SEND } from '../../api/constants';

// Teacher-side weekly game report modal (opened from the attendance page's
// Game Stats modal): view a student's AI weekly reports, step between weeks,
// edit the Note to Parent and email the report home - same flow as
// harmonydaycare-admin's Weekly Reports screen. The caller fetches the
// reports (it needs the count for its button anyway) and passes them in;
// note edits are handed back through onReportsChange so the caller's copy
// stays current.

const NOTE_MAX_LENGTH = 5000;

// apiHandler() never throws - a failed request resolves with the raw axios
// error instead, which has no .data.
export const isFailedResponse = (res) => !res || res?.isAxiosError || (res?.response && res?.data === undefined);

const formatDate = (isoDate, withTime = false) => {
  if (!isoDate) return '';
  // week_start_date is a plain YYYY-MM-DD - parse as local, not UTC midnight,
  // so it never shifts back a day in negative-offset timezones.
  const date = /^\d{4}-\d{2}-\d{2}$/.test(isoDate) ? new Date(`${isoDate}T00:00:00`) : new Date(isoDate);
  if (Number.isNaN(date.getTime())) return isoDate;
  const options = { month: 'short', day: 'numeric', year: 'numeric' };
  if (withTime) Object.assign(options, { hour: 'numeric', minute: '2-digit' });
  return date.toLocaleString('en-US', options);
};

const showToast = (icon, title) => {
  Swal.fire({
    toast: true,
    position: 'top-end',
    icon,
    title,
    showConfirmButton: false,
    timer: icon === 'success' ? 2500 : 3500,
    timerProgressBar: true,
  });
};

const SECTION_STYLES = {
  strengths: { title: 'Strengths', icon: 'star', border: '#9fe1cb', bg: '#e1f5ee', color: '#0f6e56' },
  priorities: { title: 'Priorities', icon: 'target', border: '#afa9ec', bg: '#eeedfe', color: '#4b3f9e' },
  activities: { title: 'Suggested Activities', icon: 'family_restroom', border: '#85b7eb', bg: '#e6f1fb', color: '#1d5a9e' },
};

const ReportSection = ({ type, items }) => {
  const style = SECTION_STYLES[type];
  return (
    <div className="p-3 mb-2" style={{ border: `1px solid ${style.border}`, background: style.bg, borderRadius: '10px' }}>
      <div className="d-flex align-items-center mb-1" style={{ fontSize: '11px', fontWeight: 700, color: style.color, textTransform: 'uppercase' }}>
        <i className="material-symbols-outlined me-1" style={{ fontSize: '16px' }}>{style.icon}</i>
        {style.title}
      </div>
      {items && items.length > 0 ? (
        <ul className="mb-0 ps-3" style={{ fontSize: '14px', color: '#2c2c2c' }}>
          {items.map((item, index) => <li key={index}>{item}</li>)}
        </ul>
      ) : (
        <em className="text-muted small">None</em>
      )}
    </div>
  );
};

const TeacherWeeklyReportModal = ({ show, onHide, studentName, reports = [], onReportsChange }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [noteDraft, setNoteDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [sending, setSending] = useState(false);

  // Every open starts on the latest week (reports come newest first).
  useEffect(() => {
    if (show) {
      setSelectedIndex(0);
      setNoteDraft(reports[0]?.parent_note || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const selectedReport = reports[selectedIndex] || null;
  const noteChanged = selectedReport ? noteDraft.trim() !== (selectedReport.parent_note || '') : false;
  const busy = savingNote || sending;
  const hasPreviousWeek = selectedIndex < reports.length - 1;
  const hasNextWeek = selectedIndex > 0;

  const selectReport = (index) => {
    setSelectedIndex(index);
    setNoteDraft(reports[index]?.parent_note || '');
  };

  const closeModal = () => {
    if (busy) return;
    onHide();
  };

  const updateReportNote = (reportId, parentNote) => {
    setNoteDraft(parentNote);
    if (onReportsChange) {
      onReportsChange(reports.map((r) => (r.id === reportId ? { ...r, parent_note: parentNote } : r)));
    }
  };

  const saveNote = async () => {
    if (!selectedReport) return;
    const note = noteDraft.trim();
    if (!note) {
      showToast('error', 'Note to parent cannot be empty.');
      return;
    }
    setSavingNote(true);
    const res = await apiHandler(TEACHER_GAME_REPORT_PARENT_NOTE, 'POST', { report_id: selectedReport.id, parent_note: note });
    setSavingNote(false);
    if (isFailedResponse(res)) {
      showToast('error', getApiErrorMessage(res, 'Failed to save the note.'));
      return;
    }
    updateReportNote(selectedReport.id, res?.data?.data?.parent_note ?? note);
    showToast('success', res?.data?.data?.message || 'Parent note updated successfully.');
  };

  const sendToParents = async () => {
    if (!selectedReport) return;
    const note = noteDraft.trim();
    if (!note) {
      showToast('error', 'Note to parent cannot be empty.');
      return;
    }

    const confirm = await Swal.fire({
      title: 'Email to parent?',
      text: `Email the week of ${formatDate(selectedReport.week_start_date)} report to ${studentName}'s parent?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Send',
      confirmButtonColor: '#198754',
    });
    if (!confirm.isConfirmed) return;

    // An unsaved edit is sent along and saved first, so the email matches
    // what's on screen.
    const payload = { report_id: selectedReport.id };
    if (noteChanged) payload.parent_note = note;

    setSending(true);
    const res = await apiHandler(TEACHER_GAME_REPORT_SEND, 'POST', payload);
    setSending(false);
    if (isFailedResponse(res)) {
      showToast('error', getApiErrorMessage(res, 'Failed to send the email.'));
      return;
    }
    updateReportNote(selectedReport.id, res?.data?.data?.parent_note ?? note);
    showToast('success', res?.data?.data?.message || 'Weekly report sent.');
  };

  return (
    <Modal show={show} onHide={closeModal} size="lg" centered scrollable>
      <style>{`
        .twr-week-nav { display: flex; align-items: center; gap: 8px; }
        .twr-week-nav .form-select { flex: 1; min-width: 0; max-width: 320px; }
        @media (max-width: 575.98px) {
          .twr-modal-footer { flex-direction: column-reverse; align-items: stretch !important; }
          .twr-modal-footer .btn { width: 100%; }
        }
      `}</style>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '17px', fontWeight: 700 }}>
          <i className="material-symbols-outlined me-2" style={{ fontSize: '20px', verticalAlign: 'middle', color: '#6f42c1' }}>insights</i>
          Weekly Report - {studentName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!selectedReport ? (
          <div className="text-center py-5 text-muted">No weekly report present for this student.</div>
        ) : (
          <>
            {reports.length > 1 && (
              <div className="twr-week-nav mb-3">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => selectReport(selectedIndex + 1)}
                  disabled={!hasPreviousWeek || busy}
                  title="Previous week"
                >
                  <i className="material-symbols-outlined" style={{ fontSize: '18px', verticalAlign: 'middle' }}>chevron_left</i>
                </Button>
                <Form.Select value={selectedIndex} onChange={(e) => selectReport(Number(e.target.value))} disabled={busy}>
                  {reports.map((report, index) => (
                    <option key={report.id} value={index}>
                      Week of {formatDate(report.week_start_date)}{index === 0 ? ' (latest)' : ''}
                    </option>
                  ))}
                </Form.Select>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => selectReport(selectedIndex - 1)}
                  disabled={!hasNextWeek || busy}
                  title="Next week"
                >
                  <i className="material-symbols-outlined" style={{ fontSize: '18px', verticalAlign: 'middle' }}>chevron_right</i>
                </Button>
              </div>
            )}

            <div style={{ border: '1px solid #eef0f3', borderRadius: '12px', overflow: 'hidden' }}>
              <div className="px-3 py-2" style={{ background: '#f8f9fa' }}>
                <div style={{ fontSize: '10px', color: '#aaa', textTransform: 'uppercase' }}>Week of</div>
                <div style={{ fontSize: '16px', fontWeight: 600, color: '#333' }}>{formatDate(selectedReport.week_start_date)}</div>
                {selectedReport.generated_at && (
                  <div className="text-muted" style={{ fontSize: '12px' }}>Generated {formatDate(selectedReport.generated_at, true)}</div>
                )}
              </div>
              <div className="p-3">
                {selectedReport.weekly_summary?.length > 0 && (
                  <div className="table-responsive mb-3">
                    <table className="table table-sm table-bordered mb-0">
                      <thead>
                        <tr>
                          <th>Category</th>
                          <th>Sessions</th>
                          <th>Accuracy</th>
                          <th>Difficulty Reached</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedReport.weekly_summary.map((row, index) => (
                          <tr key={index}>
                            <td>{row.category}</td>
                            <td>{row.sessionsPlayed}</td>
                            <td>{row.accuracyPercent}%</td>
                            <td>{row.difficultyReached} / 5</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <ReportSection type="strengths" items={selectedReport.strengths} />
                <ReportSection type="priorities" items={selectedReport.priorities} />
                <ReportSection type="activities" items={selectedReport.activities} />

                <div className="p-3" style={{ border: '1px solid #f3c6da', background: '#fdf1f6', borderRadius: '10px' }}>
                  <div className="d-flex align-items-center mb-2" style={{ fontSize: '11px', fontWeight: 700, color: '#a3486b', textTransform: 'uppercase' }}>
                    <i className="material-symbols-outlined me-1" style={{ fontSize: '16px' }}>mail</i>
                    Note to Parent
                    {noteChanged && <span className="ms-2 text-muted" style={{ textTransform: 'none', fontWeight: 400 }}>(unsaved changes)</span>}
                  </div>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    maxLength={NOTE_MAX_LENGTH}
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    disabled={busy}
                  />
                  <div className="text-end mt-2">
                    <Button size="sm" variant="primary" onClick={saveNote} disabled={busy || !noteChanged}>
                      {savingNote ? <Spinner animation="border" size="sm" className="me-1" /> : <i className="material-symbols-outlined me-1" style={{ fontSize: '16px', verticalAlign: 'middle' }}>save</i>}
                      Save Note
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="twr-modal-footer d-flex align-items-center gap-2">
        {selectedReport && (
          <Button variant="success" onClick={sendToParents} disabled={busy}>
            {sending ? <Spinner animation="border" size="sm" className="me-1" /> : <i className="material-symbols-outlined me-1" style={{ fontSize: '18px', verticalAlign: 'middle' }}>send</i>}
            Email to Parent
          </Button>
        )}
        <Button variant="secondary" onClick={closeModal} disabled={busy}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TeacherWeeklyReportModal;
